import * as Sentry from '@sentry/browser';

import {buildErrorCode} from './errorCodes/utils/errorCode';
import {hel_net_is_logged_in} from './logics/auth';
import {ETextParamType_SetFullBodySendingInterval, ETextParamType_SetRoomURL} from './logics/const';
import {
  getSkyRoomId,
  getWorldId,
  setSkyRoomId,
  setSkywayAvatarNumber,
  setSkywayAvatarNumberForSend,
  setUseSkywayAudioGainToggle,
  setSkywayName
} from './logics/dataOps';
import {hel_chatbadge_ui_update} from './logics/gui_caller';
import {hel_net_replace_room_url_with_roomid} from './logics/history';
import {hel_net_get_mic_device_list, hel_net_can_access_microphone} from './logics/localStream';
import {hel_setText} from './logics/moduleCaller';
import {state as monitorState, updateSentryUserCodeFromChannelSession} from './logics/monitor';
import {apiAjax} from './logics/networkLogic';
import {hel_get_cookie, hel_skyway_set_show_chatbox} from './logics/shared';
import {
  hel_skyway_release_player,
  hel_skyway_SetMixerVolume,
  hel_skyway_receive_data,
  hel_skyway_send_data,
  hel_skyway_join,
  hel_skyway_send_myinfo,
  hel_skyway_main,
  hel_skyway_send_chat_text,
  hel_skyway_add_text_chat,
  hel_skyway_to_code_colon,
  hel_skyway_set_user_name,
  hel_net_select_authorization_jwt
} from './logics/skyway';
import {
  hel_get_video_stream_use,
  hel_set_video_stream_use,
  hel_skyway_play_remote_video_stream,
  hel_skyway_stop_screen_sharing_via_room,
  hel_video_is_playing_same_stream,
  hel_video_stop,
  videoStreams
} from './logics/video_callee';
import {CHeliNetwork} from './networklib/CHeliNetwork';
import {CApiNotification} from './networklib/apiNotificationLib/CApiNotification';
import {CAvatarInfo} from './networklib/apiNotificationLib/CAvatarInfo';
import {initApiNotificationHandler} from './networklib/apiNotificationLib/initApiNotificationHandler';
import {hel_net_join_position_channel_by_lobbying} from './networklib/lobbying';
import {EHeliMicPermissionState} from './networklib/networkCallback';
import {
  joinPosChannel,
  getWorldItemStates,
  getRoomItemStates,
  getWorldItemStatesByObjectId,
  getRoomItemStatesByObjectId,
  joinVoiceChannel,
  connect,
  prepareLeavePositionChannel
} from './networklib/networkMediator';
import {CPosChannelFormatter} from './networklib/networkMediator';
import {CVoiceChannelFormatter} from './networklib/networkMediator';
import {CLiveChannelFormatter} from './networklib/networkMediator';
import {GetIHeliNetwork, singletonRegistry} from './singleton_registry';

import type {AuthorizationJwtDataListResult} from './logics/skyway';
import type {Transform, IHeliMic, IHeliMicResponse} from './networklib/CHeliNetwork';
import type {CHeliPlayer} from './networklib/CHeliPlayer';
import type {CHeliVoiceChannel} from './networklib/CHeliVoiceChannel';
import type {CHeliOldSkywayUnifiedPositionVoiceChannelProvider} from './networklib/positionChannelProviders/CHeliOldSkywayUnifiedPositionVoiceChannelProvider';
import type {CHeliSkywayVoiceChannelProvider} from './networklib/voiceChannelProviders/CHeliSkywayVoiceChannelProvider';
import type {SkywayAjaxOption} from './types/internal';
import type {AvatarInfo} from './types/network';
import type {NotifyCollectionScope, NotifyEventMap} from './types/notify';
import type {Command} from '../../HeliportJS/dist/models/ingameStateManager';
import type {JsonObject} from '../../HeliportJS/dist/models/json';
import type {RoomStream} from 'skyway-js';
import type Peer from 'skyway-js';

declare global {
  /* eslint-disable no-var -- NOTE: TypeScriptのグローバルスコープ拡張では「var」が必須 */
  var hel_if_displayChatMessage: (id: string, text: string) => void | undefined;
  var hel_if_displayChatBadge: (() => void) | undefined;
  /* eslint-enable */
}

const hel_skyway_set_avatar_from_cookie = () => {
  const avatar = hel_get_cookie('Avatar');
  if (!avatar) {
    return;
  }

  const avatarNum = Number(avatar);
  if (Number.isNaN(avatarNum)) {
    return;
  }

  setSkywayAvatarNumber(avatarNum);
  setSkywayAvatarNumberForSend(avatarNum);
};

/**
 * @remarks
 * heliodor_room.jsから移植
 * TODO アプリケーション層より
 * @param world_id
 * @param option
 */
const getRandomRoom = (world_id?: string | null, option?: SkywayAjaxOption) => {
  if (!world_id) {
    return;
  }
  apiAjax('GET', '/rooms/random/' + world_id, {
    done: option === undefined ? undefined : option.done,
    error: option === undefined ? undefined : option.error
  });
};

/** @see https://developer.mozilla.org/ja/docs/Web/JavaScript/Reference/Global_Objects/Math/random#%E5%8C%85%E6%8B%AC%E7%9A%84%E3%81%AB_2_%E3%81%A4%E3%81%AE%E5%80%A4%E3%81%AE%E9%96%93%E3%81%AE%E3%83%A9%E3%83%B3%E3%83%80%E3%83%A0%E3%81%AA%E6%95%B4%E6%95%B0%E3%82%92%E5%BE%97%E3%82%8B */
const getRandomIntInclusive = (min: number, max: number): number => {
  const minCeiled = Math.ceil(min);
  const maxFloored = Math.floor(max);
  return Math.floor(Math.random() * (maxFloored - minCeiled + 1) + minCeiled); // 上限を含み、下限も含む
};

Object.assign(globalThis, {
  hel_set_video_stream_use,

  hel_get_video_stream_use,

  /**
   * @remarks
   * heliodor_skyway_xrw, heliodor_skyway_rcpから参照があるためglobal関数
   */
  hel_skyway_release_player,

  /**
   * heliodor_skyway_rcp, heliodor_skyway_xrw, heliodor_xrw_mainから参照があるためglobal関数
   * @param peerId
   */
  hel_skyway_get_player: (peerId: string): CHeliPlayer | null => {
    return GetIHeliNetwork()?.getPlayer(peerId) ?? null;
  },

  /**
   * @deprecated
   * {@link CHeliOldSkywayUnifiedPositionVoiceChannelProvider} {@link CHeliSkywayVoiceChannelProvider} 特有のメソッド。
   * @remarks
   * heliodor_skyway_rcp, heliodor_skyway_xrwから参照があるためglobal関数
   * @param stream
   */
  hel_skyway_stream: (stream: RoomStream) => {
    GetIHeliNetwork()?.setPlayerStream(stream);
  },

  /**
   * JS, HeliScriptから参照あり
   */
  hel_skyway_get_peerID: () => {
    return GetIHeliNetwork()?.getPlayerId() ?? '';
  },

  /**
   * @remarks
   * heliodor_skyway_rcp, heliodor_skyway_xrwから参照があるためglobal関数
   */
  hel_skyway_SetMixerVolume,

  hel_skyway_GetPlayerList: () => {
    return GetIHeliNetwork()?.getPlayerList();
  },

  /**
   * heliodor_skyway_rcp, heliodor_skyway_xrwから参照があるためglobal関数
   * @param roomid
   */
  hel_skyway_set_roomid: (roomid: string) => {
    setSkyRoomId(roomid);
  },

  /**
   * heliodor_skyway_rcp, heliodor_skyway_xrwから参照があるためglobal関数
   */
  hel_skyway_get_roomid: (): string => {
    return getSkyRoomId();
  },

  /**
   * @remarks
   * heliodor_skyway_rcp, heliodor_skyway_xrwから参照があるためglobal関数
   */
  hel_skyway_set_avatar_from_cookie,

  /**
   * @remarks
   * heliodor_skyway_rcp, heliodor_skyway_xrwから参照があるためglobal関数
   */
  hel_skyway_receive_data,

  /**
   * @remarks
   * heliodor_skyway_rcp, heliodor_skyway_xrwから参照があるためglobal関数
   */
  hel_skyway_add_text_chat,

  /**
   * @remarks
   * heliodor_skyway_rcp, heliodor_skyway_xrwから参照があるためglobal関数
   */
  hel_chatbadge_ui_update,

  /**
   * heliodor_skyway_rcp, heliodor_skyway_xrw,C++から参照があるためglobal関数
   * @remarks
   * TODO
   * agora/skyway/photon/chalcedonyで今後realtimeEngineSDKに分岐
   * @param peerId
   * @param mute
   * @virtual
   */
  hel_skyway_SetVoiceMute: (peerId: string, mute: boolean) => {
    GetIHeliNetwork()?.setPlayerVoiceMute(peerId, mute);
  },

  /**
   * heliodor.js, heliodor_skyway_rcp.js, heliodor_skyway_xrw.js, C++から参照あり
   * @remarks
   * TODO
   * agora/skyway/photon/chalcedonyで今後realtimeEngineSDKに分岐
   * @param peerId
   * @param volume
   * @virtual
   */
  hel_skyway_SetVoiceAttenuate: (playerId: string, volume: number) => {
    if (typeof volume !== 'number') {
      return;
    }
    GetIHeliNetwork()?.setPlayerVoiceAttenuate(playerId, volume);
  },

  /**
   * ハートビートは、Channel Session API + heliport.jsが担うので、何もしない。
   * @remarks C++層から参照あり。
   * @deprecated C++層から参照するコードを削除すると同時に本グローバル関数も削除する。
   * @see https://discord.com/channels/596169759045976094/819383257622773811/1367333875793264712
   */
  hel_skyway_send_heartbeat: () => {},

  /**
   * @remarks
   * heliodor.js, heliodor_skyway_rcp.js, heliodor_skyway_xrw.js, C++から参照あり
   */
  hel_skyway_send_data,

  /**
   * @remarks
   * heliodor_skyway_rcp, heliodor_skyway_xrw, heliodor_xrw_mainから参照があるためglobal関数
   */
  hel_skyway_join,

  /**
   * @remarks
   * heliodor_skyway_rcp.js, heliodor_skyway_xrw.js, C++から参照あり
   */
  hel_skyway_send_myinfo,

  //

  hel_net_debug: () => {
    return GetIHeliNetwork();
  },

  /**
   * @deprecated
   * {@link CHeliOldSkywayUnifiedPositionVoiceChannelProvider} 特有のメソッドである {@link CHeliNetwork.connectRoomAfterPeerOpen} を呼び出している。
   * @remarks
   * 公開関数
   * NOTE: front.js側への公開関数はhel_netの名称で統一するため
   */
  hel_net_connect: hel_skyway_main,

  /**
   * @deprecated
   * {@link CHeliOldSkywayUnifiedPositionVoiceChannelProvider} 特有のメソッドである {@link CHeliNetwork.connectRoomAfterPeerOpen} を呼び出している。
   * @remarks
   * 公開関数
   * hel_skyway_mainと同様に接続を実行する機能だが、先にRoomIDを動的に取得する
   * @param configKey
   * @virtual
   */
  hel_net_connect_random_room: async (configKey?: string) => {
    // 空いているルームIDを取得
    getRandomRoom(getWorldId(), {
      done: (response: any) => {
        const room_id = response.data.room_id;
        setSkyRoomId(room_id);
        hel_skyway_main(configKey, true);
      },
      error: (error) => {
        console.error(`[ getRandomRoom ] error = ${error}`);
      }
    });
  },

  /**
   * 公開関数
   * ネットワークから切断する
   *
   * @remarks
   * 本関数を実行する場合、`hel_net_leave_pos_channel()` は実行しない。
   */
  hel_net_disconnect: async () => {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      return;
    }

    prepareLeavePositionChannel();
    await heliNetwork.disconnect();
  },

  /**
   * 公開関数
   * ネットワークの同期開始
   */
  hel_net_start_sync: async () => {
    GetIHeliNetwork()?.sendData('start');
  },

  /**
   * 公開関数
   * ネットワークの初期化
   */
  hel_net_initialize: async (): Promise<void> => {
    monitorState.channelSessionAlreadyInitialized = true;
    try {
      await updateSentryUserCodeFromChannelSession();
    } catch (exception) {
      console.error(exception);
      Sentry.captureException(exception);
    }

    // ネットワークライブラリが生成されていなければ生成する
    if (GetIHeliNetwork() == null) {
      singletonRegistry.heliNetwork = new CHeliNetwork();
    }

    // Firebaseの通知の初期化
    initApiNotificationHandler();
  },

  /**
   * @remarks
   * HeliodorのCanvas層 (HeliScript層) から、RTCプロバイダーを設定するために呼び出される関数。
   * それ以外からの呼び出しは禁止。
   * @returns
   */
  hel_net_set_rtc_provider: async (channelid: string): Promise<void> => {
    await GetIHeliNetwork()?.setRtcProvider(channelid);
  },

  /**
   * 公開関数
   * RoomURLの設定
   * コンフィグ画面に表示されるものです
   */
  hel_net_set_room_url: (url: string) => {
    hel_setText(ETextParamType_SetRoomURL, url);
  },

  /**
   * @remakrs
   * 公開関数
   * HeliScript(ChS-APIで使用)
   */
  hel_net_replace_room_url_with_roomid,

  /**
   * @remarks
   * C++から参照している
   */
  hel_net_get_mic_permission_state: (): EHeliMicPermissionState => {
    return GetIHeliNetwork()?.getMicPermissionState() ?? EHeliMicPermissionState.Prompt;
  },

  hel_net_check_mic_permission_state: async (): Promise<void> => {
    await GetIHeliNetwork()?.checkMicPermissionState();
  },

  /**
   * @remarks
   * HeliodorのCanvas層 (HeliScript層) から、マイクデバイスの初期化を行うために呼び出される関数。
   * それ以外からの呼び出しは禁止。
   * @returns
   */
  hel_net_init_mic_device: async (): Promise<IHeliMicResponse> => {
    return (
      (await GetIHeliNetwork()?.initMicDevice()) ?? {
        status: 'ng',
        device_info: null,
        errorCode: buildErrorCode('Platform', 'TypeScript', 'MediaStream', 'Audio', 'Other')
      }
    );
  },

  hel_net_init_mic_input: (deviceId: string | null): void => {
    GetIHeliNetwork()?.initLocalMicInput(deviceId);
  },

  hel_net_set_mic_enabled: async (enabled: boolean): Promise<void> => {
    await GetIHeliNetwork()?.setMicEnabled(enabled);
  },

  /**
   * @remarks
   * HeliodorのCanvas層 (HeliScript層) から、現在のマイクステータスを参照するために呼び出される関数。
   * それ以外からの呼び出しは禁止。
   * @returns
   */
  hel_net_get_mic_enabled: (): boolean => {
    return GetIHeliNetwork()?.getMicEnabled() ?? false;
  },

  hel_net_get_mic_device_list,
  hel_net_can_access_microphone,

  hel_net_change_mic_device: async (deviceId: string): Promise<IHeliMicResponse> => {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }
    return await heliNetwork.changeMicDevice(deviceId);
  },

  /**
   * @remarks
   * HeliodorのCanvas層 (HeliScript層) から、スピーカーモードと一般モード切替ために呼び出される関数。
   * それ以外からの呼び出しは禁止。
   * @returns
   */
  hel_net_change_speaker_mode: (speakerMode: boolean): boolean => {
    return GetIHeliNetwork()?.changeSpeakerMode(speakerMode) ?? false;
  },

  /**
   * @remarks
   * HeliodorのCanvas層 (HeliScript層) から、座標チャンネルに接続済みかどうかの判定に呼び出される関数。
   * それ以外からの呼び出しは禁止。
   * @returns
   */
  hel_net_is_position_channel_connected: () => {
    return GetIHeliNetwork()?.isConnectedPositionChannel() ?? false;
  },

  /**
   * @remarks
   * HeliodorのCanvas層 (HeliScript層) から、音声チャンネルに接続済みかどうかの判定に呼び出される関数。
   * それ以外からの呼び出しは禁止。
   * @returns
   */
  hel_net_is_voice_channel_connected: () => {
    return GetIHeliNetwork()?.isConnectedVoiceChannel() ?? false;
  },

  /**
   * @remarks
   * HeliodorのCanvas層 (HeliScript層) から、マイクデバイス変更をインゲームで扱うために呼び出される関数。
   * それ以外からの呼び出しは禁止。
   * @returns
   */
  hel_net_get_current_mic_device: (): IHeliMic | null => {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }
    return heliNetwork.getCurrentMicDevice();
  },

  hel_net_get_current_pos_channel: (): CPosChannelFormatter | null => {
    const positionChannel = GetIHeliNetwork()?.getCurrentPositionChannel();
    if (!positionChannel) {
      return null;
    }

    // HeliScriptで共通のプロパティ名で値を取得できるように整形する
    return new CPosChannelFormatter(positionChannel);
  },

  hel_net_get_position_channel: async (channelid: string): Promise<CPosChannelFormatter | null> => {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      return null;
    }
    const posChannel = await heliNetwork.getPosChannel(channelid);

    // HeliScriptで共通のプロパティ名で値を取得できるように整形する
    return new CPosChannelFormatter(posChannel);
  },

  hel_net_get_position_channel_list: async (
    spatiumcode: string,
    worldCode: string,
    offset: number,
    limit: number,
    excludeEmptyChannelFlg: boolean
  ): Promise<CPosChannelFormatter[] | null> => {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      return null;
    }

    const posChannelList = await heliNetwork.getPosChannelList(spatiumcode, worldCode, {
      offset,
      limit: limit !== -1 ? limit : null,
      excludeEmptyChannelFlg
    });

    // HeliScriptで共通のプロパティ名で値を取得できるように整形する
    return posChannelList
      .getList()
      .map((positionChannel) => new CPosChannelFormatter(positionChannel));
  },

  /**
   * @remarks
   * HeliodorのCanvas層 (HeliScript層) から呼び出される関数。
   * それ以外からの呼び出しは禁止。
   */
  hel_net_select_authorization_jwt,

  /**
   * @remarks
   * HeliodorのCanvas層 (HeliScript層) から、非同期ロビイング機能を実装するために呼び出される関数。
   * それ以外からの呼び出しは禁止。
   */
  hel_net_join_position_channel_by_lobbying,

  /**
   * @remarks
   * HeliodorのCanvas層 (HeliScript層) から、ランダム入室機能を実装するために呼び出される関数。
   * それ以外からの呼び出しは禁止。
   * @param args
   * @returns 入室可能なルームがないとき、新規ルームの作成に失敗したら、エラーコードを返す。
   */
  hel_net_join_random_position_channel: async (args: {
    spatiumCode: string;
    worldCode: string;
    excludeEmptyChannel: boolean;
    allowMultiVoiceChannel: boolean;
  }): Promise<{authorizationJwt: string | null; errorCode: string | null}> => {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    const positionChannelList = await heliNetwork.getPosChannelList(
      args.spatiumCode,
      args.worldCode,
      {offset: null, limit: null, excludeEmptyChannelFlg: args.excludeEmptyChannel}
    );

    if (!positionChannelList.isValid()) {
      console.error(
        'CHeliNetwork.getPosChannelList() failed.',
        positionChannelList.getErrorCode(),
        positionChannelList.getErrorData()
      );
    }

    const positionChannelDetails = positionChannelList
      .getList()
      .map((channel) => {
        const detail = channel.getPositionChannelDetail();
        if (!detail) {
          throw new Error(
            'CHeliNetwork.getPosChannelList() returns a list including an invalid channel.'
          );
        }
        return detail;
      })
      .filter((detail) => {
        return detail.users.length < detail.max_player_count;
      });

    const selectAuthorizationJwtArgs = {
      jwtDataList:
        (hel_net_is_logged_in()
          ? ((await heliport.v3.api.conferenceManagerApi.fetchHierarchicalAuthorizationJwts(
              args
            )) as AuthorizationJwtDataListResult)
          : null
        )?.data?.jwts ?? [],
      spatiumCode: args.spatiumCode,
      worldCode: args.worldCode
    } satisfies Parameters<typeof hel_net_select_authorization_jwt>[0];

    let positionChannelId: string;
    if (positionChannelDetails.length > 0) {
      const positionChannel =
        positionChannelDetails[getRandomIntInclusive(0, positionChannelDetails.length - 1)];
      if (!positionChannel) {
        throw new Error();
      }
      positionChannelId = positionChannel.channel_id;
    } else {
      const positionChannel = await heliNetwork.createPosChannel(args.spatiumCode, args.worldCode, {
        channelType: 'public',
        name: `${args.allowMultiVoiceChannel ? 'FreeSpace' : 'FreeTalk'}_${getRandomIntInclusive(0, 999998)}`,
        description: null
      });
      const detail = positionChannel.getPositionChannelDetail();
      if (!detail) {
        const errorCode = positionChannel.getErrorCode();
        if (!errorCode) {
          console.error(positionChannel);
          throw new Error('CHeliNetwork.createPosChannel() failed without error code.');
        }
        return {
          authorizationJwt: hel_net_select_authorization_jwt(selectAuthorizationJwtArgs),
          errorCode
        };
      }
      positionChannelId = detail.channel_id;
    }
    await heliNetwork.initializeChannelSessionContext(positionChannelId);
    const authorizationJwt = hel_net_select_authorization_jwt({
      ...selectAuthorizationJwtArgs,
      positionChannelId
    });
    await joinPosChannel(positionChannelId, authorizationJwt, null);
    return {authorizationJwt, errorCode: null};
  },

  hel_net_join_pos_channel: async (
    channelid: string,
    authorityJwt: string | null,
    ticketJwt: string | null
  ) => {
    if (channelid === null || channelid === '') return null;
    return joinPosChannel(channelid, authorityJwt, ticketJwt);
  },

  /** @remarks `hel_net_disconnect()` を実行する場合は、本関数は実行しない。 */
  hel_net_leave_pos_channel: async (): Promise<void> => {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      return;
    }

    prepareLeavePositionChannel();
    await heliNetwork.leave();
  },
  /**
   * 後方互換用のダミー関数
   */
  hel_net_create: (type: string): void => {
    return;
  },
  hel_net_create_pos_channel: async (
    spatiumCode: string,
    worldCode: string,
    channelType: string,
    name: string,
    description: string
  ) => {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      return null;
    }
    const posChannel = await heliNetwork.createPosChannel(spatiumCode, worldCode, {
      channelType,
      name,
      description
    });

    // HeliScriptで共通のプロパティ名で値を取得できるように整形する
    return new CPosChannelFormatter(posChannel);
  },

  hel_net_update_pos_channel: async (
    channelType: string,
    name: string,
    description: string
  ): Promise<CPosChannelFormatter | null> => {
    const positionChannel = GetIHeliNetwork()?.getCurrentPositionChannel();
    if (!positionChannel) {
      return null;
    }

    positionChannel.update({
      channelType,
      name,
      description
    });

    return new CPosChannelFormatter(positionChannel);
  },

  hel_net_get_current_voice_channel: (): CVoiceChannelFormatter | null => {
    const voiceChannel = GetIHeliNetwork()?.getCurrentVoiceChannel();
    if (!voiceChannel) {
      return null;
    }

    // HeliScriptで共通のプロパティ名で値を取得できるように整形する
    return new CVoiceChannelFormatter(voiceChannel);
  },

  hel_net_get_voice_channel: async (channelid: string): Promise<CVoiceChannelFormatter | null> => {
    const voiceChannel = await GetIHeliNetwork()?.getVoiceChannel(channelid);
    if (!voiceChannel) {
      return null;
    }

    // HeliScriptで共通のプロパティ名で値を取得できるように整形する
    return new CVoiceChannelFormatter(voiceChannel);
  },

  hel_net_get_voice_channel_list: async (
    poschannelid: string,
    offset: number,
    limit: number,
    excludeEmptyChannelFlg: boolean
  ): Promise<CVoiceChannelFormatter[] | null> => {
    const voiceChannelList = await GetIHeliNetwork()?.getVoiceChannelList(poschannelid, {
      channelType: null,
      name: null,
      description: null,
      offset,
      limit: limit !== -1 ? limit : null
    });
    if (voiceChannelList === undefined || voiceChannelList === null) return null;

    // HeliScriptで共通のプロパティ名で値を取得できるように整形する
    return voiceChannelList?.getList().map((voiceChannel: CHeliVoiceChannel) => {
      const channel = new CVoiceChannelFormatter(voiceChannel);

      return channel;
    });
  },

  /**
   * @remarks
   * HeliodorのCanvas層 (HeliScript層) から、ボイスチャンネルからの退室処理後に呼び出されるデフォルトボイスチャンネルに入るための関数。
   * それ以外からの呼び出しは禁止。
   * @returns
   */
  hel_net_join_default_voice_channel: async (): Promise<void> => {
    await GetIHeliNetwork()?.joinDefaultVoiceChannel();
  },

  hel_net_join_voice_channel: async (voiceChannelId: string) => {
    return joinVoiceChannel(voiceChannelId);
  },

  hel_net_leave_voice_channel: async (): Promise<void> => {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork || !heliNetwork.getCurrentVoiceChannel()) {
      return;
    }
    await heliNetwork.leaveVoiceChannel();
  },

  hel_net_create_voice_channel: async (
    posionChannelCode: string,
    channelType: string,
    name: string,
    description: string
  ): Promise<CVoiceChannelFormatter | null> => {
    const voiceChannel = await GetIHeliNetwork()?.createVoiceChannel(posionChannelCode, {
      channelType,
      name,
      description,
      offset: null,
      limit: null
    });
    if (!voiceChannel) return null;

    // HeliScriptで共通のプロパティ名で値を取得できるように整形する
    return new CVoiceChannelFormatter(voiceChannel);
  },

  hel_net_update_voice_channel: async (
    channelType: string,
    name: string,
    description: string
  ): Promise<CVoiceChannelFormatter | null> => {
    const voiceChannel = GetIHeliNetwork()?.getCurrentVoiceChannel();
    if (!voiceChannel) {
      return null;
    }

    voiceChannel.update({
      channelType,
      name,
      description,
      offset: null,
      limit: null
    });

    return new CVoiceChannelFormatter(voiceChannel);
  },

  /*
   * ライブチャンネル関連
   */
  hel_net_get_pos_channel_live_channel_list: async (
    poschannelid: string
  ): Promise<CLiveChannelFormatter[] | null> => {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      return null;
    }
    const liveChannelList = await heliNetwork.getPositionChannelLiveChannelList(poschannelid);

    // HeliScriptで共通のプロパティ名で値を取得できるように整形する
    return liveChannelList.getList().map((liveChannel) => new CLiveChannelFormatter(liveChannel));
  },

  hel_net_get_world_live_channel_list: async (
    spatiumCode: string,
    worldCode: string
  ): Promise<CLiveChannelFormatter[] | null> => {
    const liveChannelList = await GetIHeliNetwork()?.getWorldLiveChannelList(
      spatiumCode,
      worldCode
    );
    if (!liveChannelList) return null;

    // HeliScriptで共通のプロパティ名で値を取得できるように整形する
    return liveChannelList.getList().map((liveChannel) => new CLiveChannelFormatter(liveChannel));
  },
  hel_net_get_live_channel: async (channelid: string): Promise<CLiveChannelFormatter | null> => {
    const liveChannel = await GetIHeliNetwork()?.getLiveChannel(channelid);
    if (!liveChannel) {
      return null;
    }

    // HeliScriptで共通のプロパティ名で値を取得できるように整形する
    return new CLiveChannelFormatter(liveChannel);
  },
  hel_net_get_current_live_channel: (): CLiveChannelFormatter | null => {
    const liveChannel = GetIHeliNetwork()?.getCurrentLiveChannel();
    if (!liveChannel) {
      return null;
    }

    // HeliScriptで共通のプロパティ名で値を取得できるように整形する
    return new CLiveChannelFormatter(liveChannel);
  },

  /** ライブチャンネル 演者接続（発話可） */
  hel_net_live_join_channel_host: async (
    liveChannelIdOrEmptyString: string
  ): Promise<CLiveChannelFormatter | null> => {
    if (liveChannelIdOrEmptyString === '') {
      return null;
    }

    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      return null;
    }

    const liveChannel = await heliNetwork.getLiveChannel(liveChannelIdOrEmptyString);
    if (!liveChannel) {
      return null;
    }

    // デフォルトマイクデバイスをブラウザから取得
    const pIHeliMic = await GetIHeliNetwork()?.getMicDevice();
    if (!pIHeliMic) {
      return null;
    }

    // 入室
    await heliNetwork.joinLiveChannelAsHost(liveChannel, pIHeliMic);

    // HeliScriptで共通のプロパティ名で値を取得できるように整形する
    return new CLiveChannelFormatter(liveChannel);
  },

  /** ライブチャンネル 聴衆接続（聞き専） */
  hel_net_live_join_channel_audience: async (
    liveChannelIdOrEmptyString: string
  ): Promise<CLiveChannelFormatter | null> => {
    if (liveChannelIdOrEmptyString === '') {
      return null;
    }

    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      return null;
    }

    const liveChannel = await heliNetwork.getLiveChannel(liveChannelIdOrEmptyString);
    if (!liveChannel) {
      return null;
    }

    // 入室
    await heliNetwork.joinLiveChannelAsAudience(liveChannel);

    // HeliScriptで共通のプロパティ名で値を取得できるように整形する
    return new CLiveChannelFormatter(liveChannel);
  },

  hel_net_live_leave_channel: async (): Promise<void> => {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      return;
    }
    const liveChannel = heliNetwork.getCurrentLiveChannel();
    if (!liveChannel) {
      return;
    }
    await heliNetwork.leaveLiveChannel(liveChannel);
  },

  hel_net_live_prepare_video_item: async (itemName: string, materialName: string) => {
    GetIHeliNetwork()?.prepareLiveVideoItem(itemName, materialName);
  },

  hel_net_live_video_standby: () => {
    GetIHeliNetwork()?.standbyPlayLiveVideo();
  },
  hel_net_live_video_play: () => {
    GetIHeliNetwork()?.playLiveVideo();
  },

  hel_net_live_video_stop: () => {
    GetIHeliNetwork()?.stopLiveVideo();
  },
  hel_net_live_video_is_playing: () => {
    return GetIHeliNetwork()?.isPlayingLiveVideo() ?? false;
  },
  hel_net_live_video_is_play_ready: () => {
    return GetIHeliNetwork()?.isLiveVideoPlayReady();
  },
  hel_net_live_video_set_auto_play: (autoPlay: boolean) => {
    GetIHeliNetwork()?.setLiveVideoAutoPlay(autoPlay);
  },

  hel_net_live_set_mic_enabled: async (enabled: boolean): Promise<void> => {
    await GetIHeliNetwork()?.setLiveMicEnabled(enabled);
  },

  /**
   * @remarks
   * HeliodorのCanvas層 (HeliScript層) から、現在の発言者ユーザ用のマイクステータスを参照するために呼び出される関数。
   * それ以外からの呼び出しは禁止。
   * @returns
   */
  hel_net_live_get_mic_enabled: (): boolean => {
    return GetIHeliNetwork()?.getLiveMicEnabled() ?? false;
  },

  /** 音声チャンネルで使用するTransform情報 */
  hel_net_set_currentLocalTransform: (param: {player: Transform; camera: Transform}) => {
    GetIHeliNetwork()?.setCurrentLocalTransform(param);
  },

  /**
   * @deprecated
   * {@link CHeliOldSkywayUnifiedPositionVoiceChannelProvider} 特有のメソッドである {@link CHeliNetwork.connectRoomAfterPeerOpen} を呼び出している。
   * @remarks
   * g_MultiPlay(XRWorldIngame, JRWorld_web）経由で
   * multiPlayの場合に参照
   *
   * heliodor_front.jsから参照されるためglobal関数
   */
  hel_skyway_main,

  /**
   * @remarks
   * heliodor.js, heliodor_skyway_rcp.js, heliodor_skyway_xrw.js, C++から参照あり
   */
  hel_skyway_send_chat_text,

  /**
   * @remarks
   * JRWorld_webから呼出あり。
   * heliodor_front.jsから呼び出されるためglobal関数化
   */
  apiAjax,

  getRandomRoom,

  /**
   * @remarks
   * heliodor_room.jsから移植
   * TODO アプリケーション層より
   * @param world_id
   * @param option
   */
  createNewRoom: (world_id?: string | null, option?: SkywayAjaxOption) => {
    if (!world_id) {
      return;
    }
    apiAjax('POST', '/rooms', {
      data: {world_id: world_id},
      done: option === undefined ? undefined : option.done,
      error: option === undefined ? undefined : option.error
    });
  },

  /**
   * @remarks
   * heliodor_skyway_rcp, heliodor_skyway_xrwから参照があるためglobal関数
   */
  hel_skyway_to_code_colon,

  /**
   * Recovery "&colon;" to ":"
   * @remarks
   * heliodor_skyway_rcp, heliodor_skyway_xrwから参照があるためglobal関数
   * @param text
   */
  hel_skyway_to_char_colon: (text: string): string => {
    return text.replace('&colon;', ':');
  },

  /**
   * @deprecated
   * リファクタリングする以前は、
   *
   * - 旧Skywayでは何もしない
   * - それ以外では {@link CHeliNetwork.connect} を `await` なしで直接呼ぶ
   *
   * だけのメソッドであり、おそらく使われていないのではないか。
   * @see https://discord.com/channels/596169759045976094/1243065534916853930/1352544195700854814
   */
  hel_skyway_main_with_display_media: async (): Promise<void> => {
    await GetIHeliNetwork()?.connect();
  },

  /** 画面共有の開始2 */
  hel_skyway_start_screen_sharing_via_room: async (
    videoItemName: string,
    videoMaterialName: string,
    useCamera: boolean,
    useAudio: boolean
  ) => {
    GetIHeliNetwork()?.startScreenShare(videoItemName, videoMaterialName, useCamera, useAudio);
  },

  hel_skyway_stop_screen_sharing_via_room,

  /** 上記で予約した画面共有情報で再生開始する */
  hel_skyway_play_remote_video_stream_reserved: () => {
    GetIHeliNetwork()?.playScreenShareReserved();
  },

  /** 画面共有の再生開始 */
  hel_skyway_play_remote_video_stream,

  /** 画面共有の再生停止 */
  hel_skyway_stop_remote_video_stream: () => {
    hel_video_stop();
  },

  //
  hel_video_is_playing_same_stream,

  /**
   * 映像ストリームを返す
   * @remarks
   * - allがtrueの場合、全てを返す
   * - all falseの場合、ダミーではない再生可能なストリームだけ返す
   */
  hel_skyway_get_remote_video_streams: (all: boolean = false) => {
    if (all) {
      return videoStreams;
    }
    const playableStream = new Map();
    videoStreams.forEach((stream, peerId) => {
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack && videoTrack.getSettings().width && videoTrack.getSettings().frameRate > 0) {
        playableStream.set(peerId, stream);
      }
    });
    return playableStream;
  },

  hel_skyway_display_room_video: () => {
    GetIHeliNetwork()?.playScreenShare();
  },

  /**
   * @deprecated
   * 唯一 {@link CHeliNetwork.connect} の戻り値を返すグローバル関数であるが、
   * 戻り値の型に {@link CHeliOldSkywayUnifiedPositionVoiceChannelProvider} 特有の {@link Peer} が含まれるため、プロトコルに依存するコードの原因となっている。
   *
   * また、第1引数 `configKey` も {@link CHeliOldSkywayUnifiedPositionVoiceChannelProvider} 特有である。
   * @remarks
   * heliodor_xrw_mainから参照あり
   * @param configKey?
   * @returns
   */
  hel_skyway_generate_peer: connect,

  /**
   * @remarks
   * heliodor_skyway_wrwから参照あり
   * @param toggle
   *
   */
  hel_skyway_set_user_skyway_audio_gain: (toggle: boolean): void =>
    setUseSkywayAudioGainToggle(toggle),

  hel_skyway_set_user_name,

  /**
   * Heliportから参照があるためglobal関数
   * @remarks
   * TODO
   * Heliportから来た通知による発火を行う
   * @param scope
   * @param actionType
   * @param data
   * @virtual
   */
  hel_api_notification_reciever: <K extends keyof NotifyEventMap>(
    scope: NotifyCollectionScope,
    actionType: K,
    data: NotifyEventMap[K]
  ) => {
    CApiNotification.apiNotificationReciever(scope, actionType, data);
  },

  /**
   * HeliScriptから参照があるためglobal関数
   * @remarks
   * TODO
   * 自身のユーザ情報をJwtから取得する
   *
   * TODO
   * エンジン対応するまでの暫定実装
   * @virtual
   * @returns HeliScriptから扱いやすいよう、`null` の代わりに空文字列を返す。
   */
  hel_net_get_self_player_role: (): string => {
    return GetIHeliNetwork()?.getSelfPlayerRole() ?? '';
  },

  /**
   * HeliScriptから参照があるためglobal関数
   * @remarks
   * TODO
   * Heliportへプロパティの変更を伝える
   * @param refId Firebase経由で使用する値（主にメインシーンアクティビティ名/アクティビティのアイテム名）となる想定
   * @param key プロパティのキー名
   * @param value プロパティの値
   * @virtual
   */
  hel_net_send_item_sync_property: async (refId: string, key: string, value: string) => {
    return await GetIHeliNetwork()?.apiChangeItemPropertySender(refId, key, value);
  },

  /**
   * HeliScriptから参照があるためglobal関数
   * @remarks
   * TODO
   * Heliportへプロパティの変更を伝える
   * @param refId Firebase経由で使用する値（主にメインシーンアクティビティ名/アクティビティのアイテム名）となる想定
   * @param key プロパティのキー名
   * @param value プロパティの値
   * @virtual
   */
  hel_net_send_item_sync_property_multi: async (refId: string, data: Record<string, string>) => {
    return await GetIHeliNetwork()?.apiChangeItemPropertySenderMulti(refId, data);
  },

  /**
   * HeliScriptから参照があるためglobal関数
   * @remarks
   * TODO
   * HeliportへIngameStateManagerのコマンドを伝える
   * @param commands コマンド配列（JsVal配列）
   * @virtual
   */
  hel_net_send_item_sync_command: async (commands: Command[]) => {
    return await GetIHeliNetwork()?.apiCommandItemPropertySender(commands);
  },

  /**
   * HeliScriptから参照があるためglobal関数
   * @remarks
   * TODO
   * HeliportへIngameStateManagerのワールド単位のコマンドを伝える
   * @param commands コマンド配列（JsVal配列）
   * @virtual
   */
  hel_net_send_world_item_sync_command: async (commands: JsonObject[]) => {
    return await GetIHeliNetwork()?.apiCommandWorldItemPropertySender(commands);
  },

  /**
   * HeliScriptから参照があるためglobal関数
   * @remarks
   * TODO
   * ワールド単位のItemStateを返す
   * @virtual
   */
  hel_net_get_world_item_sync_property_all: () => {
    return getWorldItemStates();
  },

  /**
   * HeliScriptから参照があるためglobal関数
   * @remarks
   * TODO
   * チャンネルセッション単位のItemStateを返す
   * @virtual
   */
  hel_net_get_room_item_sync_property_all: () => {
    return getRoomItemStates();
  },

  /**
   * HeliScriptから参照があるためglobal関数
   * @remarks
   * TODO
   * 指定したオブジェクトIDのワールド単位のItemStateを返す
   * @param objectId statesのオブジェクトID、通常はアイテム名
   * @virtual
   */
  hel_net_get_world_item_sync_property_by_object_id: (objectId: string) => {
    return getWorldItemStatesByObjectId(objectId);
  },

  /**
   * HeliScriptから参照があるためglobal関数
   * @remarks
   * TODO
   * 指定したオブジェクトIDのルーム単位男の所有権の要求
   * @param objectId statesのオブジェクトID、通常はアイテム名
   * @virtual
   */
  hel_net_request_room_item_sync_ownership: async (objectId: string) => {
    return GetIHeliNetwork()?.requestRoomItemSyncOwnership([objectId]);
  },
  hel_net_request_room_item_sync_ownership_multi: async (objectIds: Array<string>) => {
    return GetIHeliNetwork()?.requestRoomItemSyncOwnership(objectIds);
  },

  /**
   * HeliScriptから参照があるためglobal関数
   * @remarks
   * TODO
   * 指定したオブジェクトIDのルーム単位男の所有権の解放
   * @param objectId statesのオブジェクトID、通常はアイテム名
   * @virtual
   */
  hel_net_release_room_item_sync_ownership: async (objectId: string) => {
    return GetIHeliNetwork()?.releaseRoomItemSyncOwnership([objectId]);
  },
  hel_net_release_room_item_sync_ownership_multi: async (objectIds: Array<string>) => {
    return GetIHeliNetwork()?.releaseRoomItemSyncOwnership(objectIds);
  },

  /**
   * HeliScriptから参照があるためglobal関数
   * @remarks
   * TODO
   * 指定したオブジェクトIDのチャンネルセッション単位のItemStateを返す
   * @param objectId statesのオブジェクトID、通常はアイテム名
   * @virtual
   */
  hel_net_get_room_item_sync_property_by_object_id: (objectId: string) => {
    return getRoomItemStatesByObjectId(objectId);
  },

  /**
   * 指定したユーザのChannelSessionCodeを取得する、nullの場合は自身のを返す
   * @remarks
   * HeliScriptから参照があるためglobal関数
   * @virtual
   */
  hel_net_get_channel_session_code_by_player_id: (playerId: string | null): string | undefined => {
    return GetIHeliNetwork()?.getChannelSessionCodeByPlayerId(playerId);
  },

  /**
   * 指定したチャンネルセッションコードのPlayerIdを取得する、nullの場合は自身のを返す
   * @remarks
   * HeliScriptから参照があるためglobal関数
   * @virtual
   */
  hel_net_get_player_id_by_channel_session_code: (channelSessionCode: string | null): string => {
    return GetIHeliNetwork()?.getPlayerIdByChannelSessionCode(channelSessionCode) ?? '';
  },

  /**
   * C++からTS層に他プレーヤのアバター表示切替コールバック
   * @param playerId プレーヤID
   * @param isFullAvatar true:フルアバター表示 false: ダミーアバター表示
   * @virtual
   */
  helcpp_on_player_fullavatar_state_change: async (
    playerId: string,
    isFullAvatar: boolean
  ): Promise<void> => {
    GetIHeliNetwork()?.setPlayerFullAvatarState(playerId, isFullAvatar);
  },

  /**
   * C++からTS層に他プレーヤのモーショントラッキング使用状況切替コールバック
   * @param playerId プレーヤID
   * @param isFullAvatar true:フルアバター表示 false: ダミーアバター表示
   * @virtual
   */
  helcpp_on_player_motion_tracking_state_change: async (
    playerId: string,
    isMotionTracking: boolean
  ): Promise<void> => {
    GetIHeliNetwork()?.setPlayerMotionTrackingState(playerId, isMotionTracking);
  },

  /**
   * @remarks
   * フルボディモーショントラッキングデータの送信間隔を設定(初期値は 125.0 ms)
   * @param timeMS 送信間隔(単位はミリセカンド)
   */
  hel_net_set_fullbody_sending_interval: (timeMS: number) => {
    hel_setText(ETextParamType_SetFullBodySendingInterval, timeMS.toString());
  },

  /* アバター関連 */

  /**
   * 自分のアバターを、指定されたMyAvatarへ変更する。
   * @remarks
   * HeliodorのHeliScript層から呼び出されるとともに、コンテンツ側からも呼び出されることを想定した関数。
   * @param avatarInfo `null` が指定されていれば、プリセットアバターへ変更したことを表す。
   */
  hel_net_set_my_avatar: (avatarInfo: AvatarInfo | null): Promise<void> => {
    return CAvatarInfo.setOrUnsetCurrentAvatarInfo(avatarInfo);
  },

  /**
   * 現在自分が使用しているMyAvatarのUUIDを返す。
   * @remarks
   * HeliodorのHeliScript層から、アバター選択画面を構築するために呼び出される関数。
   * コンテンツ側からの呼び出しは禁止。
   * @returns プリセットアバターを使用している場合は、`null` を返す。
   */
  hel_net_get_my_avatar: (): {uuid: string} | null => {
    const avatarInfo = CAvatarInfo.getCurrentAvatarInfo();
    return avatarInfo ? {uuid: avatarInfo.uuid} : null;
  }
});

// 即時実行
const cookie_Skyway_MyName = hel_get_cookie('playername');
if (cookie_Skyway_MyName !== undefined) {
  setSkywayName(cookie_Skyway_MyName);
}
hel_skyway_set_show_chatbox(false);
hel_skyway_set_avatar_from_cookie();
